// BrainThink — watch the brain think. One component for the plane and the
// kit (byte-identical, scope: both). It plays the think stream of one turn
// (backend/shared/retrievalTrace.js `thinkCollector`: ordered events
// `{ seq, at, type, … }` — the legs that ran, the pages each found, what
// fusion kept, what sibling demotion moved, what the answer cited, which
// brain pages NoVA walked) over the BrainMap, with a stage ribbon, a sources
// rail for everything that is not a wiki page, and a scrubber.
//
//   <BrainThink graph={graph} events={events} live={bool} height={420}
//               hrefFor={(docId) => href} onOpen={(node) => …}
//               labels={true} rail={true} view="map" picker={true}
//               heat={docId→count} trails={[recent traces]} stamp={true} />
//
// Without a graph (a member on the plane, a visitor, a voice turn) the map
// gives way to the constellation: the docs the stream named, around the
// question, radius by phase. `labels={false}` draws unlabeled dots and a
// counts caption (the public counts-only mode); `rail={false}` drops the
// sources rail (a narrow column).
//
// Views (components/ThinkViews.js): the same fold drawn another way — map /
// constellation, sonar, evidence curve, bar race, timing waterfall, ribbons,
// tape, scorecard; heat grid when `heat` and a graph are given, trails when
// `trails` (recent traces) is. `picker` shows the view switcher (keys 1–9);
// `stamp` keeps the verdict pill (searched → kept → cited) under every view.
//
// Live: the cursor follows the newest event as it arrives. Replay: events
// are paced at their recorded offsets, floored at 150 ms and capped at
// 1.2 s, so a 900 ms retrieval is watchable and a 40 s answer does not
// stall. The rule: animate only what was emitted — never interpolate. Under
// prefers-reduced-motion a replay jumps to the end and nothing pulses.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BrainMap from './BrainMap.js';
import { Scorecard, EvidenceCurve, BarRace, Waterfall, Tape, Sonar, Ribbons, HeatGrid, Trails } from './ThinkViews.js';
import './BrainThink.css';

const STRATUM_LABEL = { 'app-brain': "this app's brain", brain: 'project brain', docs: 'product docs', design: 'app design', 'kit-docs': 'kit docs', org: 'documents' };
const LEG_LABEL = { vector: 'meaning', keyword: 'words', graph: 'related' };
const STAGES = [
  ['ask', 'question', (c) => c['ask.start'] || c['brain.search'] || c['leg.start']],
  ['rewrite', 'rephrase', (c) => c['query.rewrite']],
  ['legs', 'search', (c) => c['leg.start'] || c['brain.search']],
  ['fuse', 'fuse', (c) => c.fuse],
  ['siblings', 'siblings', (c) => c.siblings],
  ['rerank', 'rerank', (c) => c.rerank],
  ['select', 'select', (c) => c.select],
  ['context', 'context', (c) => c.context || c['brain.open'] || c.guide || c.lookup || c.tool],
  ['answer', 'answer', (c) => c['answer.start']],
  ['cited', 'cited', (c) => c['answer.done']],
];
const PHASE_RANK = { candidate: 1, selected: 2, opened: 3, cited: 4 };
const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// A knowledge id → the map node key. Brain pages are `brain-<slug>` (the
// plane keys nodes by slug; the kit by `<stratum>:<slug>`), the app's own
// brain is `layer-brain-<slug>` (kit stratum `app`). Anything else is not on
// the map and lives in the sources rail.
function keyResolver(graph) {
  const bySlug = new Map();
  for (const n of graph?.nodes || []) {
    const key = n.key || n.slug;
    const entry = { key, stratum: n.stratum || 'brain', title: n.title };
    // Plane ids are `brain-<slug>`; kit ids are `brain-<folder>-<slug>`
    // (brainView.knowledgeIdOf) — index both spellings.
    for (const s of n.folder ? [n.slug, `${n.folder}-${n.slug}`] : [n.slug]) {
      const list = bySlug.get(s) || [];
      list.push(entry);
      bySlug.set(s, list);
    }
  }
  return (docId) => {
    const id = String(docId || '');
    let slug = null; let want = null;
    if (id.startsWith('layer-brain-')) { slug = id.slice('layer-brain-'.length); want = 'app'; } else if (id.startsWith('brain-')) { slug = id.slice('brain-'.length); want = ['brain', 'kit']; }
    if (!slug) return null;
    const list = bySlug.get(slug);
    if (!list) return null;
    const hit = list.find((x) => (Array.isArray(want) ? want.includes(x.stratum) : x.stratum === want)) || list[0];
    return hit.key;
  };
}

// Describe one event in a line.
export function describeEvent(ev) {
  if (!ev) return '';
  switch (ev.type) {
    case 'ask.start': return `Asked: “${ev.question || ''}”`;
    case 'query.rewrite': return `Also asking: ${(ev.variants || []).slice(1).map((v) => `“${v}”`).join(', ')}`;
    case 'leg.start': return `Searching by ${LEG_LABEL[ev.leg] || ev.leg}${ev.variant ? ` (variant ${ev.variant + 1})` : ''}${ev.words?.length ? `: ${ev.words.join(' ')}` : ''}`;
    case 'leg.hits': return `${LEG_LABEL[ev.leg] || ev.leg} found ${ev.total ?? ev.hits?.length ?? 0}`;
    case 'fuse': return `Fused ${ev.candidates} candidates, keeping ${ev.kept}`;
    case 'siblings': return `${ev.demoted} sibling page${ev.demoted === 1 ? '' : 's'} moved behind (one subject)`;
    case 'rerank': return `Reranking the top ${ev.k}`;
    case 'select': return ev.after === 'rerank' ? 'Reranked order' : `Selected ${(ev.hits || []).filter((h) => h.used).length} sources`;
    case 'context': return `${ev.blocks} block${ev.blocks === 1 ? '' : 's'}, ${Number(ev.chars || 0).toLocaleString()} characters handed to the model`;
    case 'brain.search': return `Searched the brain for “${ev.query || ''}” — ${ev.slugs?.length || 0} pages`;
    case 'brain.open': return `Opened ${ev.title || ev.slug}${ev.section ? ` › ${ev.section}` : ''}`;
    case 'tool': return `Ran ${ev.name}`;
    case 'guide': return `Read the console guide${ev.module ? ` for ${ev.module}` : ''}`;
    case 'lookup': return `Looked up ${ev.entity || 'records'}${ev.op && ev.op !== 'list' ? ` (${ev.op})` : ''}${ev.n != null ? ` — ${ev.n}` : ''}`;
    case 'answer.start': return 'Writing the answer';
    case 'answer.done': return ev.declined ? 'Declined (policy)' : ev.empty ? 'Nothing relevant found' : `Answered in ${((ev.ms || 0) / 1000).toFixed(1)}s, citing ${ev.citations?.length || 0}`;
    default: return ev.type;
  }
}

// Fold events[0..cursor] into what the map and the rail draw.
export function foldThink(events, cursor, keyOf) {
  const docs = new Map(); // docId → { docId, title, stratum, phase, legs:Set }
  const bump = (docId, phase, meta = {}) => {
    if (!docId) return;
    const cur = docs.get(docId) || { docId, title: meta.title || docId, stratum: meta.stratum || (String(docId).startsWith('brain-') ? 'brain' : 'org'), phase: 'candidate', legs: new Set() };
    if (meta.title && cur.title === docId) cur.title = meta.title;
    if (meta.stratum) cur.stratum = meta.stratum;
    if (meta.leg) cur.legs.add(meta.leg);
    for (const l of meta.legs || []) cur.legs.add(l);
    if ((PHASE_RANK[phase] || 0) > (PHASE_RANK[cur.phase] || 0)) cur.phase = phase;
    docs.set(docId, cur);
  };
  let touched = [];
  let focusDoc = null;
  const counts = {};
  for (let i = 0; i <= cursor && i < events.length; i += 1) {
    const ev = events[i];
    counts[ev.type] = (counts[ev.type] || 0) + 1;
    touched = [];
    if (ev.type === 'leg.hits') for (const h of ev.hits || []) { bump(h.docId, 'candidate', { ...h, leg: ev.leg }); touched.push(h.docId); }
    else if (ev.type === 'leg.start' && ev.seeds) for (const h of ev.seeds) touched.push(h.docId);
    else if (ev.type === 'select') for (const h of ev.hits || []) { bump(h.docId, h.used ? 'selected' : 'candidate', h); if (h.used) touched.push(h.docId); }
    else if (ev.type === 'context') for (const d of ev.docIds || []) { bump(d, 'selected'); touched.push(d); }
    else if (ev.type === 'brain.search') for (const h of ev.hits || []) { bump(h.docId, 'candidate', h); touched.push(h.docId); }
    else if (ev.type === 'brain.open') { bump(ev.docId, 'opened', { title: ev.title, stratum: 'brain' }); touched.push(ev.docId); focusDoc = ev.docId; }
    else if (ev.type === 'answer.done') for (const d of ev.citations || []) { bump(d, 'cited'); touched.push(d); }
  }
  const phases = {};
  const offMap = [];
  for (const d of docs.values()) {
    const k = keyOf(d.docId);
    if (k) phases[k] = d.phase; else offMap.push(d);
  }
  const pulse = new Set(touched.map(keyOf).filter(Boolean));
  const focus = focusDoc ? keyOf(focusDoc) : null;
  return { docs: [...docs.values()], phases, pulse, touched: new Set(touched), focus, offMap, counts, current: events[Math.min(cursor, events.length - 1)] || null };
}

// The stage a stream has reached (index into STAGES, -1 before the first
// event) and its label — the dock's pulse strip colours by this.
export function stageOf(counts) {
  let last = -1;
  STAGES.forEach(([, , test], i) => { if (test(counts || {})) last = i; });
  return { index: last, id: last >= 0 ? STAGES[last][0] : null, label: last >= 0 ? STAGES[last][1] : '' };
}

// One line about a folded stream — the public stage's caption; true under
// counts-only payloads too (no titles needed).
export function summarizeThink(fold) {
  if (!fold) return '';
  const legs = new Set();
  for (const d of fold.docs) for (const l of d.legs) legs.add(l);
  const searched = fold.docs.length;
  const kept = fold.docs.filter((d) => PHASE_RANK[d.phase] >= PHASE_RANK.selected).length;
  const cited = fold.docs.filter((d) => d.phase === 'cited').length;
  const opened = fold.docs.filter((d) => d.phase === 'opened').length;
  if (!searched) {
    if (fold.counts['ask.start'] || fold.counts['brain.search']) return 'Searching…';
    // A turn that never searched still did something — say what.
    if (fold.counts.guide) return 'Read the console guide.';
    if (fold.counts.lookup) return 'Looked up your records.';
    if (fold.counts.tool) return `Ran ${fold.counts.tool === 1 ? 'a tool' : `${fold.counts.tool} tools`}.`;
    return '';
  }
  const by = legs.size ? ` across ${[...legs].map((l) => LEG_LABEL[l] || l).join(' · ')}` : '';
  return `Searched ${searched} source${searched === 1 ? '' : 's'}${by}${kept ? `, kept ${kept}` : ''}${opened ? `, opened ${opened}` : ''}${cited ? `, cited ${cited}` : ''}.`;
}

// ---- the constellation ----------------------------------------------------------
// The picture when there is no map to light up (a member on the plane, a
// visitor, a voice turn): the docs the stream itself named, laid out around
// the question. Each stratum owns an arc; a doc's angle inside it is a hash
// of its id (stable from event to event); its radius is its phase — a
// candidate on the rim, selected closer, cited closest, an opened page at
// the centre. Nothing is drawn that was not emitted.
const STRATUM_ORDER = ['brain', 'app-brain', 'docs', 'kit-docs', 'design', 'org'];
const PHASE_R = { candidate: 0.92, selected: 0.6, cited: 0.34, opened: 0.14 };
function hash01(s) { let h = 2166136261; for (let i = 0; i < s.length; i += 1) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return ((h >>> 0) % 10007) / 10007; }
function Constellation({ fold, height, width = 600, labels = true, live = false, noMotion = false, hrefFor = null, LinkComponent = 'a' }) {
  const Link = LinkComponent;
  const W = width; const H = Math.max(160, height);
  const cx = W / 2; const cy = H / 2; const R = Math.min(W, H) / 2 - 18;
  const layout = useMemo(() => {
    const byStratum = new Map();
    for (const d of fold.docs) { const l = byStratum.get(d.stratum) || []; l.push(d); byStratum.set(d.stratum, l); }
    const strata = [...byStratum.keys()].sort((a, b) => (STRATUM_ORDER.indexOf(a) + 99) % 99 - (STRATUM_ORDER.indexOf(b) + 99) % 99);
    const total = fold.docs.length || 1;
    const nodes = []; const arcs = [];
    let a0 = -Math.PI / 2;
    for (const s of strata) {
      const list = byStratum.get(s).slice().sort((a, b) => hash01(a.docId) - hash01(b.docId));
      const span = (list.length / total) * Math.PI * 2;
      const start = a0;
      arcs.push({ stratum: s, mid: start + span / 2, count: list.length });
      for (let i = 0; i < list.length; i += 1) {
        const d = list[i];
        const ang = start + ((i + 0.5) / list.length) * span;
        const r = R * (PHASE_R[d.phase] || PHASE_R.candidate);
        nodes.push({ ...d, x: cx + Math.cos(ang) * r, y: cy + Math.sin(ang) * r, ang });
      }
      a0 += span;
    }
    return { nodes, arcs };
  }, [fold.docs, cx, cy, R]);
  const searching = live && !fold.counts['answer.done'];
  return (
    <svg className={`bt-const ${noMotion ? 'no-motion' : ''}`} viewBox={`0 0 ${W} ${H}`} style={{ height }} role="img" aria-label={summarizeThink(fold)}>
      <g className="bt-const-rings">
        {['candidate', 'selected', 'cited'].map((p) => <circle key={p} cx={cx} cy={cy} r={R * PHASE_R[p]} className={`bt-ring is-${p}`} />)}
      </g>
      {labels ? layout.arcs.map((a) => (
        <text key={a.stratum} x={cx + Math.cos(a.mid) * (R + 10)} y={cy + Math.sin(a.mid) * (R + 10)} className="bt-const-arc" textAnchor={Math.cos(a.mid) < -0.2 ? 'end' : Math.cos(a.mid) > 0.2 ? 'start' : 'middle'} dominantBaseline="middle">{STRATUM_LABEL[a.stratum] || a.stratum} · {a.count}</text>
      )) : null}
      {layout.nodes.filter((d) => PHASE_RANK[d.phase] >= PHASE_RANK.selected).map((d) => (
        <line key={`l-${d.docId}`} x1={cx} y1={cy} x2={d.x} y2={d.y} className={`bt-const-line is-${d.phase}`} />
      ))}
      <circle cx={cx} cy={cy} r={6} className={`bt-const-q ${searching ? 'is-searching' : ''}`} />
      {layout.nodes.map((d) => {
        const hot = !noMotion && fold.touched.has(d.docId);
        const href = labels && hrefFor ? hrefFor(d.docId) : null;
        const show = labels && PHASE_RANK[d.phase] >= PHASE_RANK.selected;
        const right = Math.cos(d.ang) >= 0;
        const dot = (
          <g key={d.docId} className={`bt-const-node is-${d.phase} ${hot ? 'is-hot' : ''}`} style={{ transform: `translate(${d.x}px, ${d.y}px)` }}>
            {hot ? <circle r={14} className="bt-const-pulse" /> : null}
            <circle r={d.phase === 'candidate' ? 3.5 : d.phase === 'selected' ? 5 : 6} />
            {show ? <text x={right ? 9 : -9} y={3} textAnchor={right ? 'start' : 'end'} className="bt-const-label">{String(d.title).length > 34 ? `${String(d.title).slice(0, 33)}…` : d.title}</text> : null}
          </g>
        );
        return href ? <Link key={d.docId} to={href} href={href} className="bt-const-link">{dot}</Link> : dot;
      })}
    </svg>
  );
}

// The views on offer, in picker order. `map` is the BrainMap when a graph
// is present, the constellation otherwise; heat needs both a graph and a
// heat object; trails needs recent traces.
export const THINK_VIEWS = [
  ['map', 'map', 'the brain map, or the constellation of sources around the question'],
  ['sonar', 'sonar', 'time as the angle: sources appear where the hand was when they were found'],
  ['curve', 'curve', 'distinct sources over time, with kept / used / cited as drops'],
  ['race', 'race', 'candidates as bars, re-sorting as fusion and rerank land'],
  ['waterfall', 'timing', 'a bar per leg, an instant per step, the answer'],
  ['ribbons', 'ribbons', 'legs → sources → outcome'],
  ['tape', 'tape', 'the stream as a log'],
  ['score', 'score', 'the verdict frame'],
  ['heat', 'heat', 'the whole corpus as cells; served lately as heat, this search lit'],
  ['trails', 'trails', 'the last searches as footprints: which pages keep being served'],
];

export default function BrainThink({ graph, events = [], live = false, height = 420, hrefFor = null, onOpen = null, LinkComponent = 'a', autoplay = true, labels = true, rail = true, view: initialView = 'map', picker = null, heat = null, trails = null, stamp = true, onView = null, empty = null }) {
  const n = events.length;
  const [cursor, setCursor] = useState(live ? n - 1 : -1);
  const [playing, setPlaying] = useState(!live && autoplay && !reducedMotion());
  const [view, setViewState] = useState(initialView);
  const timer = useRef(null);
  const keyOf = useMemo(() => keyResolver(graph), [graph]);
  const noMotion = reducedMotion();
  const hasMap = graph && (graph.nodes || []).length > 0;
  const showPicker = picker == null ? rail && labels : picker;
  const views = useMemo(() => THINK_VIEWS.filter(([id]) => (id === 'heat' ? hasMap && heat : id === 'trails' ? Array.isArray(trails) && trails.length : true)), [hasMap, heat, trails]);
  const setView = useCallback((v) => { setViewState(v); if (onView) onView(v); }, [onView]);
  useEffect(() => { if (!views.some(([id]) => id === view)) setViewState('map'); }, [views, view]);

  // Live: follow the tail; when a live stream ends (live → false under the
  // same key, its recorded events arriving), rest on the final state. Replay:
  // pace through the recorded offsets.
  const wasLive = useRef(live);
  useEffect(() => { if (live || wasLive.current) setCursor(n - 1); wasLive.current = live; }, [live, n]);
  useEffect(() => {
    if (live || !playing) return undefined;
    if (noMotion) { setCursor(n - 1); setPlaying(false); return undefined; }
    if (cursor >= n - 1) { setPlaying(false); return undefined; }
    const next = events[cursor + 1];
    const prev = cursor >= 0 ? events[cursor] : null;
    const gap = prev ? Math.max(150, Math.min(1200, (next?.at || 0) - (prev.at || 0))) : 150;
    timer.current = setTimeout(() => setCursor((c) => Math.min(c + 1, n - 1)), gap);
    return () => clearTimeout(timer.current);
  }, [live, playing, cursor, n, events, noMotion]);

  const fold = useMemo(() => foldThink(events, cursor, keyOf), [events, cursor, keyOf]);
  const pulse = noMotion ? null : fold.pulse;
  const stageIdx = useMemo(() => { let last = -1; STAGES.forEach(([, , test], i) => { if (test(fold.counts)) last = i; }); return last; }, [fold.counts]);
  const stages = useMemo(() => STAGES.filter(([id, , test], i) => i <= 2 || test(fold.counts) || ['select', 'answer', 'cited'].includes(id)), [fold.counts]);

  const step = useCallback((d) => { setPlaying(false); setCursor((c) => Math.max(0, Math.min(n - 1, c + d))); }, [n]);
  const onKey = (e) => {
    if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) && e.target.type !== 'range') return;
    if (e.key === ',' || e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
    if (e.key === '.' || e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    if (e.key === ' ') { e.preventDefault(); setPlaying((p) => !p); }
    if (showPicker && /^[1-9]$/.test(e.key) && views[Number(e.key) - 1]) { e.preventDefault(); setView(views[Number(e.key) - 1][0]); }
  };
  const replay = () => { setCursor(-1); setPlaying(true); };
  const Link = LinkComponent;
  const groups = useMemo(() => {
    const m = new Map();
    for (const d of fold.docs) { const g = m.get(d.stratum) || []; g.push(d); m.set(d.stratum, g); }
    return [...m.entries()].map(([s, list]) => [s, list.sort((a, b) => (PHASE_RANK[b.phase] || 0) - (PHASE_RANK[a.phase] || 0))]);
  }, [fold.docs]);
  const current = fold.current;
  const line = describeEvent(current);
  // The pictures are SVGs drawn in a viewBox; give them the column's real
  // width so a 220px dock column and a 900px card both draw at 1:1.
  const mapRef = useRef(null);
  const [measured, setMeasured] = useState(0);
  const mounted = n > 0; // the empty state renders no column to measure
  useEffect(() => {
    const el = mapRef.current;
    if (!mounted || !el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => { const w = Math.round(entries[0]?.contentRect?.width || 0); if (w) setMeasured(w); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [mounted]);
  const width = measured || (rail ? 600 : 360);
  const inFlight = live || cursor < n - 1; // a replay mid-way reads as "searching" too

  if (!n) return <div className="bt bt-empty">{live ? 'Waiting for the search to start…' : (empty || 'No think stream on this answer.')}</div>;

  const picture = (() => {
    switch (view) {
      case 'sonar': return <Sonar fold={fold} events={events} cursor={cursor} width={width} height={height} labels={labels} live={live} noMotion={noMotion} hrefFor={hrefFor} LinkComponent={LinkComponent} />;
      case 'curve': return <div className="bt-pane"><EvidenceCurve events={events} cursor={cursor} width={width} height={Math.min(height, 300)} live={live} /></div>;
      case 'race': return <BarRace events={events} cursor={cursor} fold={fold} labels={labels} hrefFor={hrefFor} LinkComponent={LinkComponent} rows={Math.max(6, Math.floor((height - 22) / 26))} />;
      case 'waterfall': return <div className="bt-pane"><Waterfall events={events} cursor={cursor} width={width} live={live} /></div>;
      case 'ribbons': return <Ribbons fold={fold} width={width} height={height} labels={labels} hrefFor={hrefFor} LinkComponent={LinkComponent} />;
      case 'tape': return <Tape events={events} cursor={cursor} live={live} labels={labels} noMotion={noMotion} />;
      case 'score': return <Scorecard fold={fold} events={events} cursor={cursor} live={inFlight} />;
      case 'heat': return <HeatGrid graph={graph} heat={heat} fold={fold} keyOf={keyOf} onOpen={onOpen} labels={labels} />;
      case 'trails': return <Trails traces={trails || []} fold={fold} width={width} labels={labels} hrefFor={hrefFor} LinkComponent={LinkComponent} />;
      default: return hasMap
        ? <BrainMap graph={graph} phases={fold.phases} pulse={pulse} focus={fold.focus} chrome={false} height={height} onOpen={onOpen} />
        : <div className="bt-map-const"><Constellation fold={fold} height={height} width={width} labels={labels} live={live} noMotion={noMotion} hrefFor={hrefFor} LinkComponent={LinkComponent} /></div>;
    }
  })();
  const railOn = rail && labels && ['map', 'sonar', 'curve', 'waterfall'].includes(view);

  return (
    <div className={`bt ${live ? 'is-live' : ''} is-view-${view}`} tabIndex={0} onKeyDown={onKey} aria-label="Watch the brain think" data-nova-note={`Think player (${view} view): ${line}`}>
      <div className="bt-top">
        <ol className="bt-ribbon" aria-label="Stages">
          {stages.map(([id, label], i) => {
            const idx = STAGES.findIndex((s) => s[0] === id);
            const state = idx < stageIdx ? 'done' : idx === stageIdx ? 'now' : 'todo';
            return <li key={id} className={`bt-stage is-${state}`}>{i ? <i /> : null}<span>{label}</span></li>;
          })}
        </ol>
        {showPicker ? (
          <div className="bt-views" role="tablist" aria-label="View">
            {views.map(([id, label, help], i) => <button key={id} type="button" role="tab" aria-selected={view === id} className={`bt-view ${view === id ? 'is-on' : ''}`} onClick={() => setView(id)} title={`${help} (${i + 1})`}>{label}</button>)}
          </div>
        ) : null}
      </div>
      <div className="bt-body">
        <div className="bt-map" ref={mapRef}>{picture}</div>
        {railOn ? <aside className={`bt-rail ${hasMap && view === 'map' ? '' : 'is-const'}`} aria-label="Sources">
          {groups.length ? groups.map(([s, list]) => (
            <div key={s} className="bt-group">
              <h5>{STRATUM_LABEL[s] || s} <span>{list.length}</span></h5>
              <ul>
                {list.slice(0, hasMap && view === 'map' ? 8 : 14).map((d) => {
                  const href = hrefFor ? hrefFor(d.docId) : null;
                  const hot = !noMotion && fold.touched.has(d.docId);
                  return (
                    <li key={d.docId} className={`bt-src is-${d.phase} ${hot ? 'is-hot' : ''}`}>
                      <i />
                      {href ? <Link to={href} href={href}>{d.title}</Link> : <span>{d.title}</span>}
                      <em>{d.phase}{d.legs.size ? ` · ${[...d.legs].map((l) => LEG_LABEL[l] || l).join(', ')}` : ''}</em>
                    </li>
                  );
                })}
              </ul>
            </div>
          )) : <div className="bt-group bt-none">No sources yet</div>}
        </aside> : null}
      </div>
      <div className="bt-bar">
        {live ? <span className="bt-live"><i className="cs-dot live" /> live</span> : (
          <>
            <button type="button" className="bt-btn" onClick={() => (cursor >= n - 1 ? replay() : setPlaying((p) => !p))} aria-label={playing ? 'Pause' : 'Play'}>{cursor >= n - 1 ? '↺' : playing ? '❚❚' : '▶'}</button>
            <button type="button" className="bt-btn" onClick={() => step(-1)} aria-label="Previous event" disabled={cursor <= 0}>‹</button>
            <button type="button" className="bt-btn" onClick={() => step(1)} aria-label="Next event" disabled={cursor >= n - 1}>›</button>
          </>
        )}
        <input className="bt-scrub" type="range" min={0} max={Math.max(0, n - 1)} value={Math.max(0, cursor)} onChange={(e) => { setPlaying(false); setCursor(Number(e.target.value)); }} aria-label="Event" disabled={live} />
        <span className="bt-count">{Math.max(0, cursor) + 1}/{n}{current?.at != null ? ` · ${(current.at / 1000).toFixed(1)}s` : ''}</span>
      </div>
      <div className="bt-foot">
        <p className="bt-line" role="status">{labels ? line : summarizeThink(fold) || line}</p>
        {stamp ? <Scorecard fold={fold} events={events} cursor={cursor} live={inFlight} compact /> : null}
      </div>
    </div>
  );
}
